import React from 'react';
import Barcode from 'react-barcode';

/* Checkbox — identical style to existing consignment note */
const Chk = ({ checked }) => (
  <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center ${checked ? 'bg-black' : ''}`}>
    {checked && <span className="text-white text-[10px]">✓</span>}
  </div>
);

const ChkLabel = ({ checked, label }) => (
  <div className="flex items-center gap-1">
    <Chk checked={checked} />
    <span className="text-xs font-semibold">{label}</span>
  </div>
);

const SectionRow = ({ children, cols, className = '' }) => (
  <div className={`border border-black mb-1 ${className}`}>
    <div className={`grid ${cols} divide-x divide-black`}>
      {children}
    </div>
  </div>
);

const Cell = ({ children, className = '' }) => (
  <div className={`p-1.5 ${className}`}>{children}</div>
);

const GrayLabel = ({ children }) => (
  <span className="text-[10px] font-bold text-gray-700 uppercase block mb-0.5">{children}</span>
);

const RoadRailV2Print = ({ printData }) => {
  const { docket, company, resolved_parties, resolved_shipment } = printData;

  const hdr  = docket.payload_json?.header          || {};
  const pkgD = docket.payload_json?.package_details || {};
  const parD = docket.payload_json?.party_details   || {};
  const invD = docket.payload_json?.invoice_details || {};
  const vehD = docket.payload_json?.vehicle_details || {};

  const cnNo  = docket.system_docket_no || '';
  const dwbNo = docket.printed_docket_no || cnNo;

  const fmt = (d) => d
    ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN',
        { day:'2-digit', month:'2-digit', year:'numeric' }).replace(/\//g,'-')
    : '';

  const svc  = hdr.service_type || '';
  const mode = hdr.mode         || '';
  const load = hdr.load_type    || '';
  const frt  = hdr.billing_type || '';

  const pickup      = resolved_shipment?.loading_from || '';
  const destination = resolved_shipment?.delivery_at  || '';

  const consignor = resolved_parties?.exporter_shipper  || {};
  const consignee = resolved_parties?.importer_receiver || {};
  const barcodeVal = (cnNo || dwbNo || '0').replace(/[^A-Z0-9\-_]/gi, '');

  return (
    <>
    <div style={{ fontFamily: 'Arial, sans-serif' }}>

      {/* ══ HEADER — identical layout to existing consignment note ════════ */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-4">
          {/* Logo */}
          <div>
            {company?.logo_url ? (
              <img src={company.logo_url} alt="logo"
                style={{ height:64, width:'auto', objectFit:'contain' }} />
            ) : (
              <div className="w-20 h-16 bg-gray-200 flex items-center justify-center text-xs text-gray-500 border">
                LOGO
              </div>
            )}
          </div>
          {/* Company info */}
          <div className="flex-1">
            <h1 className="text-lg font-bold uppercase">
              {company?.name || 'STAVAN LOGISTICS PRIVATE LIMITED'}
            </h1>
            <p className="text-xs mt-1 leading-tight max-w-md">
              {company?.address || '737-738 Sun Gravitas, Radio Mirchi Road, Vejalpur, Ahmedabad 380051, Gujarat, India'}
            </p>
            {company?.phone && <p className="text-xs mt-0.5">Tel: {company.phone}</p>}
            {company?.email && <p className="text-xs">Email: {company.email}</p>}
            {company?.gst_number && (
              <p className="text-xs font-semibold mt-0.5">GSTIN: {company.gst_number}</p>
            )}
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-base font-bold">CONSIGNMENT NOTE</h2>
          <p className="text-[9px] text-gray-500 mt-0.5">Consignor / Consignee / POD / Transporter Copy</p>
        </div>
      </div>

      {/* ══ ROW 1 — CN NO + BARCODE + DATE ══════════════════════════════ */}
      <div className="border border-black mb-1">
        <div className="grid grid-cols-2 divide-x divide-black">
          {/* Left — LR NO + Barcode */}
          <div className="p-1.5">
            <div className="text-xs font-bold mb-1">
              LR NO.:{' '}
              <span className="text-blue-700 text-sm">{cnNo}</span>
            </div>
            {barcodeVal && (
              <Barcode value={barcodeVal} width={1.5} height={40}
                fontSize={9} margin={0} displayValue={false} format="CODE128" />
            )}
          </div>
          {/* Right — Date + Manual No */}
          <div className="p-1.5">
            <GrayLabel>Date &amp; Time</GrayLabel>
            <p className="text-xs font-semibold">{fmt(hdr.date)}</p>
            {dwbNo && (
              <>
                <GrayLabel>Manual No.:</GrayLabel>
                <p className="text-xs font-semibold">{dwbNo}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ══ ROW 2 — CONSIGNOR + CONSIGNEE ═══════════════════════════════ */}
      <div className="border border-black mb-1">
        <div className="grid grid-cols-2 divide-x divide-black">
          {/* Consignor */}
          <div className="p-1.5">
            <div className="text-xs font-bold border-b border-gray-300 pb-1 mb-1">
              CONSIGNOR (FROM)
            </div>
            <p className="text-xs font-semibold">{consignor.name || ''}</p>
                        <p className="text-[10px] whitespace-pre-wrap mt-0.5">
              {parD.pickup_address || consignor.address || ''}
            </p>
            {consignor.phone && <p className="text-[10px]">Tel: {consignor.phone}</p>}
            {consignor.email && <p className="text-[10px]">Email: {consignor.email}</p>}
          </div>
          {/* Consignee */}
          <div className="p-1.5">
            <div className="text-xs font-bold border-b border-gray-300 pb-1 mb-1">
              CONSIGNEE (TO)
            </div>
            <p className="text-xs font-semibold">{consignee.name || ''}</p>
                        <p className="text-[10px] whitespace-pre-wrap mt-0.5">
              {parD.delivery_address || consignee.address || ''}
            </p>
            {consignee.phone && <p className="text-[10px]">Tel: {consignee.phone}</p>}
            {consignee.email && <p className="text-[10px]">Email: {consignee.email}</p>}
          </div>
        </div>
      </div>

      {/* ══ ROW 3 — PICK UP + DESTINATION ════════════════════════════════ */}
      <SectionRow cols="grid-cols-2">
        <Cell>
          <GrayLabel>PICK UP</GrayLabel>
          <span className="text-xs font-semibold">{pickup}</span>
        </Cell>
        <Cell>
          <GrayLabel>DESTINATION</GrayLabel>
          <span className="text-xs font-semibold">{destination}</span>
        </Cell>
      </SectionRow>

      {/* ══ ROW 4 — MODE + SERVICE ═══════════════════════════════════════ */}
      <SectionRow cols="grid-cols-2">
        {/* Mode */}
        <Cell className="flex items-center gap-4">
          <span className="text-xs font-bold bg-gray-100 px-2 py-0.5 rounded">MODE</span>
          <ChkLabel checked={mode==='Surface'} label="SURFACE" />
          <ChkLabel checked={mode==='Air'}     label="AIR" />
          <ChkLabel checked={mode==='Rail'}    label="RAIL" />
        </Cell>
        {/* Service */}
        <Cell className="flex items-center gap-4">
          <span className="text-xs font-bold bg-gray-100 px-2 py-0.5 rounded">SERVICE</span>
          <ChkLabel checked={svc==='General'} label="GENERAL" />
          <ChkLabel checked={svc==='Express'} label="EXPRESS" />
          <ChkLabel checked={svc==='Refer'}   label="REEFER" />
        </Cell>
      </SectionRow>

      {/* ══ ROW 5 — LOAD TYPE + FREIGHT FLAG ════════════════════════════ */}
      <SectionRow cols="grid-cols-2">
        <Cell className="flex items-center gap-4">
          <span className="text-xs font-bold bg-gray-100 px-2 py-0.5 rounded">LOAD TYPE</span>
          <ChkLabel checked={load==='FTL'} label="FTL" />
          <ChkLabel checked={load==='PTL'} label="PTL" />
        </Cell>
        {/* Freight */}
        <Cell className="flex items-center gap-4">
          <span className="text-xs font-bold bg-gray-100 px-2 py-0.5 rounded">FREIGHT</span>
          <ChkLabel checked={frt==='TBB'}  label="TBB" />
          <ChkLabel checked={frt==='FOD'}  label="FOD" />
          <ChkLabel checked={frt==='PAID'} label="PAID" />
        </Cell>
      </SectionRow>

      {/* ══ ROW 6 — VEHICLE DETAILS (moved above package table) ══════════ */}
      <div className="border border-black mb-1">
        <div className="grid grid-cols-4 divide-x divide-black bg-gray-100 border-b border-black">
          {['VEH. NO.','DATA LOGGER NO.','TEMPERATURE','ODA LOCATION'].map(h => (
            <div key={h} className="p-1.5 text-[10px] font-bold">{h}</div>
          ))}
        </div>
        <div className="grid grid-cols-4 divide-x divide-black">
          {[vehD.reg_no, vehD.data_logger_no, vehD.temperature,
            pkgD.oda_location_name || pkgD.oda_delivery].map((v,i) => (
            <div key={i} className="p-1.5 text-xs">{v || ''}</div>
          ))}
        </div>
      </div>

      {/* ══ ROW 7 — PACKAGE TABLE ════════════════════════════════════════ */}
      <div className="border border-black mb-1">
        {/* Header */}
        <div className="grid grid-cols-4 divide-x divide-black bg-gray-100 border-b border-black">
          {['PACKAGE TYPE','NO. OF PKGS','ACTUAL WT. KG','CHARGE WT. KG'].map(h => (
            <div key={h} className="p-1.5 text-[10px] font-bold text-center">{h}</div>
          ))}
        </div>
        {/* Data */}
        <div className="grid grid-cols-4 divide-x divide-black">
          {[pkgD.package_type, pkgD.no_of_packages, pkgD.actual_weight, pkgD.charge_weight].map((v,i) => (
            <div key={i} className="p-1.5 text-xs font-semibold text-center">{v || ''}</div>
          ))}
        </div>
      </div>

      {/* ══ ROW 8 — DESCRIPTION OF GOODS ════════════════════════════════ */}
      <div className="border border-black mb-1 p-1.5">
        <div className="flex items-center gap-2 mb-0.5">
          <GrayLabel>DESCRIPTION OF GOODS</GrayLabel>
          <span className="text-[9px] font-bold text-gray-500 uppercase">(SAID TO CONTAIN)</span>
        </div>
        <p className="text-xs mt-0.5 min-h-[20px]">{pkgD.description_of_goods || ''}</p>
      </div>

      {/* ══ ROW 9 — INVOICE + EWAY BILL ══════════════════════════════════ */}
      <div className="border border-black mb-1">
        <div className="grid grid-cols-2 divide-x divide-black bg-gray-100 border-b border-black">
          <div className="p-1.5 text-[10px] font-bold">INVOICE DETAILS</div>
          <div className="p-1.5 text-[10px] font-bold">E-WAY BILL DETAILS</div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-black">
          <div className="p-1.5">
            <div className="flex flex-wrap gap-4">
              <div>
                <GrayLabel>Invoice No.</GrayLabel>
                <span className="text-xs">{invD.invoice_no || ''}</span>
              </div>
              <div>
                <GrayLabel>Date</GrayLabel>
                <span className="text-xs">{invD.date || ''}</span>
              </div>
              <div>
                <GrayLabel>Invoice Value</GrayLabel>
                <span className="text-xs">{invD.invoice_value ? `₹${invD.invoice_value}` : ''}</span>
              </div>
              <div>
                <GrayLabel>Insurance</GrayLabel>
                <span className="text-xs">{invD.insurance || ''}</span>
              </div>
            </div>
          </div>
          <div className="p-1.5">
            <div className="flex flex-wrap gap-4">
              <div>
                <GrayLabel>E Way Bill No.</GrayLabel>
                <span className="text-xs">{invD.eway_bill_no || ''}</span>
              </div>
              <div>
                <GrayLabel>Issued Date</GrayLabel>
                <span className="text-xs">{invD.eway_bill_issued_date || ''}</span>
              </div>
              <div>
                <GrayLabel>Expiry Date</GrayLabel>
                <span className="text-xs">{invD.eway_bill_expiry_date || ''}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ ROW 10 — DECLARATION + BARCODE (swapped: Declaration left, Barcode right) ══ */}
      <div className="border border-black mb-1">
        <div className="grid grid-cols-2 divide-x divide-black">
          <div className="p-1.5">
            <GrayLabel>RECEIVER DECLARATION</GrayLabel>
            <p className="text-[10px] leading-snug mt-0.5">
              I/We hereby declare that the goods described herein are received in good
              condition and as per the consignment note.
            </p>
          </div>
          <div className="p-1.5">
            <GrayLabel>DWB. NO. &amp; BARCODE</GrayLabel>
            {barcodeVal && (
              <Barcode value={barcodeVal} width={1.5} height={44}
                fontSize={10} margin={2} displayValue={true} format="CODE128" />
            )}
          </div>
        </div>
      </div>

      {/* ══ FOOTER ═══════════════════════════════════════════════════════ */}
      <div className="border border-black">
        <div className="grid grid-cols-2 divide-x divide-black border-b border-black"
          style={{ minHeight: '4rem' }}>
          {/* Left — RECEIVER SEAL pinned to bottom-left */}
          <div className="p-1.5 flex items-end justify-start">
            <span className="text-[10px] font-bold">RECEIVER SEAL &amp; SIGNATURE</span>
          </div>
          {/* Right — FOR COMPANY pinned to bottom-right */}
          <div className="p-1.5 flex items-end justify-end">
            <span className="text-[10px] font-bold">
              FOR, {(company?.name || 'STAVAN LOGISTICS PRIVATE LIMITED').toUpperCase()}
            </span>
          </div>
        </div>
        <div className="border-b border-black">
          <div className="p-1 text-[10px] font-bold text-center">
            SUBJECT TO AHMEDABAD JURISDICTION
          </div>
        </div>
        <div className="mt-1 text-center pb-1">
          <p className="text-[9px] font-semibold text-gray-600 tracking-wide">
            ISO 9001:2015 &amp; GDP Certified Company
          </p>
          <p className="text-[9px] text-gray-500 mt-0.5">
            This is computer generated document does not require signature
          </p>
        </div>
      </div>

    </div>

    {/* ══ PAGE 2 — TERMS AND CONDITIONS ════════════════════════════════ */}
    <div style={{ pageBreakBefore: 'always', fontFamily: 'Arial, sans-serif', padding: '32px 40px' }}>
      <h2 style={{ textAlign: 'center', fontSize: '13px', fontWeight: 'bold', marginBottom: '12px' }}>
        Terms and Conditions STAVAN LOGISTICS PVT. LTD. (REFER &quot;SLPL&quot;)
      </h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {[
          'SLPL only carries goods owned by the shipper, who warrants they are authorized to accept these conditions and indemnifies SLPL against damages from any breach of warranty.',
          'SLPL is not liable for loss due to uncontrollable events like floods, accidents, civil unrest, or acts of authorities. SLPL also won\'t be liable for consequential loss under such circumstances.',
          'SLPL ensures timely pickup/delivery but isn\'t liable for delays regardless of the cause.',
          'Any damages/shortages must be reported in writing within 48 hours on writing; otherwise, complaints won\'t be entertained.',
          'SLPL reserves the right to choose its routes, which may change over time.',
          'SLPL reserves the right to inspect any documents or consignments.',
          'SLPL has a lien on goods for freight charges, customs, or any dues and may withhold delivery until these are paid.',
          'POD can be requested within 30 days of dispatch; otherwise, it won\'t be provided.',
          'Except when specifically provided, delivery will be made only to the consignee or designated person. SLPL won\'t be liable for non-receipt of delivery notices.',
          'The shipper must comply with laws and regulations for the consignment\'s transportation and provide necessary documentation.',
          'All consignments are transported under OWNER\'S RISK unless mentioned CARRIER RISK (currently 5%) are paid.',
          'Consignors should declare the value and insure their goods to cover loss, damage, or delay.',
          'Any government taxes or charges are extra and payable by the shipper or consignee. SLPL\'s liability for loss or damage is limited to Rs.100/-.',
          'Disputes are subject to arbitration in Ahmedabad, and all other litigations are under Ahmedabad\'s jurisdiction.',
          'Delivery status can be checked via SLPL offices or its website www.stavanlogistics.com.',
          'Ms. STAVAN LOGISTICS PVT. LTD. operates through decentralized regions; daily operations are managed by regional heads.',
          'SLPL is not liable for damage due to pilferage, theft, weather, strikes, or accidents.',
          'Goods must be collected from SLPL\'s godown within a week of arrival, or rent will be charged per KG/day.',
          'SLPL is not responsible if goods are detained, seized, or confiscated by authorities. The consignor is liable for transport and incidental charges.',
          'If a Consignor accepts the Lorry Receipt for advances or discounts, SLPL remains liable to Consignor for the goods\' value.',
          'SLPL will deliver the goods in the same condition, considering natural deterioration, to the consignee or bank upon surrender of the receipt.',
          'SLPL may entrust goods to another carrier but remains responsible for the goods\' safety and delivery.',
          'The consignor is liable for transport and incidental charges at SLPL\'s Head Office or an agreed location.',
          'SLPL may dispose of perishable goods after 48 hours or other goods after 30 days, after giving UGC notice.',
          'Proceeds from sales of uncollected goods will be handed over to the claimant after deducting freight and storage charges.',
          'The consignee accepting the DWB NO. isn\'t liable for the consignor\'s dues on other accounts.',
          'SLPL shall deliver goods unconditionally to consignee on payment of normal freight, without claiming lien on other dues.',
          'SLPL is responsible for due delivery and any loss or damage arising from its negligence or fraudulent actions.',
          'The consignor is liable for any consequences of incorrect or false declarations.',
          'Compensation for lost or damaged goods is limited to the value declared.',
        ].map((term, i) => (
          <li key={i} style={{ fontSize: '11px', lineHeight: '1.5', marginBottom: '3px', display: 'flex', gap: '6px' }}>
            <span style={{ flexShrink: 0 }}>•</span>
            <span>{term}</span>
          </li>
        ))}
      </ul>
    </div>
    </>
  );
};

export default RoadRailV2Print;
